import { Injectable, NgZone } from '@angular/core'
import { Observable, of, throwError } from 'rxjs'
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators'
import * as lodash from 'lodash'
import {
  RateService,
  RateView,
  Rental,
  RentalBaseRate,
  RentalRateItem,
  RentalRates,
  selectRentalEntities,
  toMoment,
} from '@tokeet-frontend/tv3-platform'
import { Store, select } from '@ngrx/store'

export type RentalRatesByCategory = Record<string, Record<string, Record<string, RentalRateItem>>>
@Injectable({ providedIn: 'root' })
export class RatesService {
  loading = true

  private rateCategories: string[] = []

  // rateViews[category][rental_id][date] = rateView
  private rateViews: Record<string, Record<string, Record<string, RateView>>>

  // rentalId => category => date => rate
  private ratesCache: Record<string, Record<string, Record<string, RentalRateItem>>> = {}

  // rateViews[rental_id] = rateView
  private baseRateViews: Record<string, RateView>

  private loadedRentals: Record<string, boolean> = {}

  private rentalBaseRates: Record<string, RentalBaseRate> = {}

  constructor(private rateService: RateService, private zone: NgZone, private store: Store<any>) {
    this.store.pipe(select(selectRentalEntities)).subscribe((rentals: Record<string, Rental>) => {
      this.rentalBaseRates = lodash.mapValues(rentals || {}, (rental) => rental?.baseRate)
    })
  }

  fetchRates(
    rentalIds: string[],
    refresh = false
  ): Observable<{
    rates: RentalRates[]
    rateCategories: string[]
    rateViews: Record<string, Record<string, Record<string, RateView>>>
    baseRateViews: Record<string, RateView>
  }> {
    if (refresh) {
      this.loadedRentals = {}
      this.rateCategories = []
      this.rateViews = {}
      this.baseRateViews = {}
    }

    const shouldFetchRates = rentalIds.filter((rentalId) => !this.loadedRentals[rentalId]).length > 0

    if (!shouldFetchRates) return of(null)

    return of(null).pipe(
      tap(() => {
        this.loading = true

        rentalIds.forEach((rentalId) => {
          this.loadedRentals[rentalId] = true
        })
      }),
      switchMap(() => this.rateService.all(rentalIds)),
      map((rates: any) => {
        if (!rates) return null

        this.rateViews = lodash.merge({}, this.rateViews, this.rateService.getRateViews(rates))
        this.baseRateViews = lodash.merge({}, this.baseRateViews, this.rateService.getBaseRateViews(rates))
        this.rateCategories = lodash.uniq(this.rateCategories.concat(this.rateService.getRateCategories(rates)))

        return {
          rates,
          rateCategories: this.rateCategories,
          rateViews: this.rateViews,
          baseRateViews: this.baseRateViews,
        }
      }),
      catchError((error) => {
        rentalIds.forEach((rentalId) => {
          this.loadedRentals[rentalId] = false
        })

        return throwError(error)
      }),
      finalize(() => {
        this.zone.run(() => {
          this.loading = false
        })
      })
    )
  }

  fetchRatesV2(rentalIds: string[], start: number, end: number, refresh = false): Observable<RentalRatesByCategory> {
    const startDate = toMoment(start).startOf('month')
    const endDate = toMoment(end).endOf('month')
    start = startDate.unix()
    end = endDate.unix()

    if (!refresh) {
      const months = lodash.times(endDate.diff(startDate, 'months') + 1, (i) =>
        startDate.clone().add(i, 'month').unix()
      )
      rentalIds = lodash.filter(rentalIds, (t) => months.some((d) => !this.ratesCache[t]?.['default']?.[d]))

      if (!rentalIds.length) return of(this.ratesCache)
    }

    this.loading = true
    return this.rateService.getRates(rentalIds, start, end, []).pipe(
      map((data) => {
        const ratesView = lodash.mapValues(
          lodash.keyBy(data?.items, (t) => t.rental_id),
          ({ categories }) => {
            return lodash.mapValues(categories, (rates) => {
              // Group rates by normalized start time (start of day)
              const grouped = lodash.groupBy(rates, (r) => toMoment(r.start).startOf('day').unix())

              return lodash.mapValues(grouped, (rateGroup) => {
                // Pick the most specific rate for the day deterministically:
                // 1) prefer higher minimum stay (stricter rules beat fallbacks),
                // 2) then higher price, to avoid randomly falling back to base rate entries.
                const [bestRate] = lodash.orderBy(
                  rateGroup,
                  [(r) => Number(r.minimumstay ?? -1), (r) => Number(r.price ?? 0)],
                  ['desc', 'desc']
                )

                return bestRate || rateGroup[0]
              })
            })
          }
        )

        // Rentals that have at least one standard/dynamic rate entry (identified by pkey)
        // should display exactly what gets pushed to channels — no base rate weekend overlay.
        // The overlay is only appropriate when the rental has no standard/dynamic rates at all
        // (pure base rate scenario). TV4-5825: dynamic rates without day-specific Sat/Sun
        // overrides were incorrectly showing the base rate weekend price on the calendar.
        const rentalsWithStandardRates = new Set(
          (data?.items || [])
            .filter(({ categories }) =>
              Object.values(categories as Record<string, RentalRateItem[]>).some((rates) =>
                (rates as RentalRateItem[]).some((r) => r.pkey)
              )
            )
            .map(({ rental_id }) => rental_id)
        )

        const adjustedRatesView = lodash.mapValues(ratesView, (categories, rentalId) =>
          rentalsWithStandardRates.has(rentalId)
            ? categories // has standard/dynamic rates → show as-is, same as what is pushed to channels
            : lodash.mapValues(categories, (rates) =>
                lodash.mapValues(rates, (rate) => this.applyWeekendBaseRate(rentalId, rate))
              )
        )
        this.ratesCache = lodash.merge({}, this.ratesCache, adjustedRatesView)

        return this.ratesCache
      }),
      finalize(() => (this.loading = false))
    )
  }

  getRate(rateCategory: string, rentalId: string, date: number): RateView {
    const rentalRateViews: any = lodash.get(this.rateViews, [rateCategory, rentalId])

    return lodash.get(rentalRateViews, date) || lodash.get(this.baseRateViews, rentalId) || null
  }

  getRates(rentalId: string, date: number) {
    const rates: RateView[] = lodash.map(this.rateViews, (rentals) => {
      return lodash.get(rentals, [rentalId, date])
    })

    return rates
  }

  getRatePrice(rate: RateView, date: number) {
    if (!rate) return null
    return this.rateService.isWeekend(date) && rate.weekend ? rate.weekend : rate.price
  }

  private applyWeekendBaseRate(rentalId: string, rate: RentalRateItem) {
    if (!rate || !!rate.pkey) return rate
    const baseRate = this.getBaseRate(rentalId)

    const rateDate = Number(rate.start)
    if (Number.isFinite(rateDate) && this.rateService.isWeekend(rateDate)) {
      const weekendPrice = baseRate?.weekend
      const nightlyPrice = baseRate?.nightly
      // TV4-5858: only substitute the weekend base rate when the day is actually
      // showing the base nightly rate. RateGenie/dynamic rates also come back
      // without a pkey, so guarding on pkey alone clobbered their per-day prices
      // on weekends (e.g. a €720 genie rate rendered as the €99 weekend base rate).
      // A base-rate placeholder always equals the rental's nightly base; a dynamic
      // rate does not.
      if (!lodash.isNil(weekendPrice) && !lodash.isNil(nightlyPrice) && rate.price === nightlyPrice) {
        return {
          ...rate,
          price: weekendPrice,
        }
      }
    }

    return rate
  }

  private getBaseRate(rentalId: string): RentalBaseRate {
    return this.rentalBaseRates?.[rentalId]
  }
}
